import type { MySubmission, SubmissionStatus, SubmissionType } from '@innspel/core';
import { Ikon } from '../Ikon.tsx';
import { formaterFrist, statusVisning } from './status.ts';
import { tekst } from './tekster.ts';

/**
 * Sakens gang, for innsenderen (R7.3, portal-konsept v0.1 · DD-08 og DD-28 presisert
 * 2026-09-20). Panelets `Sporingslinje` fra R1.4 bor i `panel/src/innboks/` og kan ikke
 * deles — dette er portalens egen, og dermed samme komponent i BEGGE utganger av
 * `@innspel/web` (snapshot-testen «samme komponent i portal og pakke»).
 *
 * Reglene, som wireframen spesifiserer dem:
 *
 * - Bare ÅPNE saker har linje. En avsluttet sak får pillen og én linje — det er ikke noe
 *   igjen å spore (DD-08). Den som kaller avgjør; {@link sporingsledd} gir tom liste.
 * - Leddene utledes av `status`, og INGEN ny kontrakt: datoene som finnes er `createdAt`
 *   (Mottatt), `statusChangedAt` (nå-leddet) og `deadlineAt` (Vurderes).
 * - **Et passert ledd mellom dem har ingen dato.** Kontrakten bærer bare den SISTE
 *   statusendringen, så «Vurderes 6. sep» på en Planlagt-sak finnes ikke som felt.
 *   Konseptsiden tegnet en dato der; å vise den ville krevd et nytt felt, og en gjettet
 *   dato er verre enn ingen (avvik ført i R7.3-PR-en).
 * - SISTE ledd har et TALL bare når kontrakten har datoen — `deadlineAt`, som finnes
 *   kun i Vurderes. Tallet er skjermens helt (DD-67). Ellers står leddet uten tall:
 *   DD-28 forbyr en ETA, og «i vinter» fra et svar er ikke et felt.
 * - **Er fristen passert, sier leddet det** (R7.4, 2026-09-20): «Fristen er gått» med
 *   varselikon i gult, datoen fortsatt som tall. Regelen er klientens — `deadlineAt < nå`
 *   i Vurderes — og krever ingen kontraktendring: serveren endrer ikke status ved frist
 *   (`domain-model.md`, «ingen automatisk statusendring»). Gult og ikke rødt: fristen er
 *   leietakerens løfte, ikke innsenderens feil (DD-53 presisert 2026-09-19). Widgeten får
 *   de samme ordene i R8.3.
 * - Nå-leddet bærer statusens ikon OG etikett fra temaet (DD-08): linjen erstatter
 *   pillen, den skjuler ikke statusen.
 */

export type LeddTilstand = 'gjort' | 'naa' | 'kommer';

export interface Ledd {
  nokkel: string;
  etikett: string;
  tilstand: LeddTilstand;
  /** Ionicons-navn fra temaet. Bare nå-leddet har det; passerte har haken. */
  ikon?: string;
  /** Linja under etiketten: datoen som finnes, eller hva som skjer. Aldri en gjettet dato. */
  detalj?: string;
  /** Datoen som tall — skjermens helt. Finnes bare på siste ledd i Vurderes (`deadlineAt`). */
  tall?: string;
  /** R7.4: fristen er passert. Leddet får varselikon og gult; tallet står. */
  fristGaatt?: true;
}

/** Hovedlinjen i tilstandsmaskinen, slik innsenderen ser den (domain-model.md). */
const HOVEDLINJE: SubmissionStatus[] = ['ny', 'vurderes', 'planlagt', 'bygges', 'levert'];

/** Statusene som fortsatt er i bevegelse. Avsluttede (`complete`/`closed`) har ingen linje. */
function erApen(rad: MySubmission): boolean {
  if (rad.pending === true) return false;
  return rad.statusType === 'reviewing' || rad.statusType === 'active';
}

/**
 * Leddene for en åpen sak. Tom liste = ingen linje (avsluttet sak, køet rad, eller en
 * status utenfor hovedlinjen som ikke har en gang å spore).
 */
export function sporingsledd(rad: MySubmission, tenantName?: string, tidspunkt: Date = new Date()): Ledd[] {
  if (rad.pending === true || !erApen(rad)) return [];

  const type: SubmissionType = rad.type;
  const navn = (status: SubmissionStatus) => statusVisning(status, type).etikett;
  const ikon = (status: SubmissionStatus) => statusVisning(status, type).ikon;
  const ledd: Ledd[] = [];

  // «Kjent feil» står utenfor hovedlinjen: saken er bekreftet mot en kjent feil, og det
  // eneste som gjenstår er at den rettes.
  if (rad.status === 'kjent_feil') {
    ledd.push({ nokkel: 'ny', etikett: navn('ny'), tilstand: 'gjort', detalj: formaterFrist(rad.createdAt) });
    ledd.push({ nokkel: 'kjent_feil', etikett: navn('kjent_feil'), tilstand: 'naa', ikon: ikon('kjent_feil'), detalj: formaterFrist(rad.statusChangedAt) });
    ledd.push({ nokkel: 'slutt', etikett: navn('levert'), tilstand: 'kommer', detalj: tekst.sporIngenDato });
    return ledd;
  }

  const naa = HOVEDLINJE.indexOf(rad.status);
  if (naa < 0) return [];

  for (let i = 0; i <= naa; i++) {
    const status = HOVEDLINJE[i]!;
    const erNaa = i === naa;
    ledd.push({
      nokkel: status,
      etikett: navn(status),
      tilstand: erNaa ? 'naa' : 'gjort',
      ...(erNaa ? { ikon: ikon(status) } : {}),
      // Mottatt har `createdAt`, nå-leddet har `statusChangedAt`. Et passert ledd mellom
      // dem har ingen dato i kontrakten, og får derfor ingen.
      ...(i === 0 ? { detalj: formaterFrist(rad.createdAt) } : erNaa ? { detalj: formaterFrist(rad.statusChangedAt) } : {}),
    });
  }

  // En sak i Mottatt får vise hva som kommer — det er det eneste stedet linjen peker forbi
  // nå-leddet, fordi «Vurderes» er handlingen innsenderen venter på.
  if (rad.status === 'ny') {
    ledd.push({
      nokkel: 'vurderes',
      etikett: navn('vurderes'),
      tilstand: 'kommer',
      detalj: tenantName ? tekst.sporNaarTenantTarDen(tenantName) : tekst.sporNaarDenTasOpp,
    });
  }

  // Siste ledd. `reviewing` venter på et SVAR (fristen er tidsløftet); `active` venter på
  // at det blir levert, og der finnes ingen dato i kontrakten.
  if (rad.statusType === 'reviewing') {
    // R7.4: fristen kan være passert. Serveren flytter ikke status ved frist, så det er
    // klienten som ser det — og da sier leddet det med ord, ikke bare med en dato i fortid.
    // `!= null`, ikke `!== undefined`: API-et skriver et utelatt felt som `null`, og
    // `new Date(null)` er epoch — en ny sak sto med «Fristen er gått» (drift 2026-09-27).
    const gaatt = rad.deadlineAt != null && new Date(rad.deadlineAt).getTime() < tidspunkt.getTime();
    ledd.push({
      nokkel: 'slutt',
      etikett: gaatt ? tekst.sporFristenErGaatt : tekst.sporSvarInnen,
      tilstand: 'kommer',
      ...(gaatt ? { fristGaatt: true, ikon: 'warning-outline' } : {}),
      ...(rad.deadlineAt ? { tall: formaterFrist(rad.deadlineAt) } : { detalj: tekst.sporDatoenSettes }),
    });
  } else {
    ledd.push({ nokkel: 'slutt', etikett: navn('levert'), tilstand: 'kommer', detalj: tekst.sporIngenDato });
  }

  return ledd;
}

export function Sporingslinje({ ledd, testId }: { ledd: Ledd[]; testId?: string }) {
  if (ledd.length === 0) return null;
  return (
    <ol className="innspel-spor" aria-label={tekst.sporEtikett} data-testid={testId}>
      {ledd.map((l, i) => (
        <li
          key={l.nokkel}
          className="innspel-spor-ledd"
          data-tilstand={l.tilstand}
          data-frist-gaatt={l.fristGaatt ? 'true' : undefined}
          data-siste={i === ledd.length - 1 ? 'true' : undefined}
        >
          <span className="innspel-spor-node" aria-hidden="true">
            {l.tilstand === 'gjort' ? <Ikon navn="checkmark-outline" /> : l.ikon ? <Ikon navn={l.ikon} /> : null}
          </span>
          <span className="innspel-spor-navn">{l.etikett}</span>
          {l.tall ? (
            <span className="innspel-spor-tall" data-testid={testId ? `${testId}-tall` : undefined}>
              {l.tall}
            </span>
          ) : l.detalj ? (
            <span className="innspel-spor-detalj">{l.detalj}</span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
